import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Users,
  Building2,
  BarChart3,
  Settings,
  Shield,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Menu,
  Tags,
  Bookmark,
  Ruler,
  FileText,
  DollarSign,
  Store,
  Truck,
  ClipboardList,
  Wallet,
  Calculator,
  Keyboard,
  X,
  Moon,
  Sun,
  ReceiptText,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useThemeStore } from '@/stores/theme-store';
import { SessionExpiredModal } from '@/components/SessionExpiredModal';
import { ToastContainer } from '@/components/ToastContainer';
import { dialogs } from '@/stores/dialog-store';
import { useSocketSync } from '@/hooks/useSocketSync';


const SHORTCUTS = [
  { key: 'Ctrl+K', desc: 'Abrir ayuda de atajos' },
  { key: 'F6', desc: 'Guardar cotización (POS)' },
  { key: 'F7', desc: 'Ventas del día (POS)' },
  { key: 'F8', desc: 'Retiros del día (POS)' },
  { key: 'F9', desc: 'Alternar precio mayoreo (POS)' },
  { key: 'F11', desc: 'Cargar venta (POS)' },
];

const allNavigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, permissions: [] },
  { name: 'Punto de Venta', href: '/pos', icon: ShoppingCart, permissions: ['create_sales'] },
  {
    name: 'Productos',
    href: '/products',
    icon: Package,
    permissions: ['read_products'],
    children: [
      { name: 'Catálogo', href: '/products', icon: Package, permissions: ['read_products'] },
      { name: 'Categorías', href: '/categories', icon: Tags, permissions: ['read_categories'] },
      { name: 'Marcas', href: '/brands', icon: Bookmark, permissions: ['read_brands'] },
      { name: 'Unidades', href: '/units', icon: Ruler, permissions: ['read_products'] },
    ],
  },
  {
    name: 'Inventario',
    href: '/inventory/quick-entry',
    icon: ClipboardList,
    permissions: ['read_products'],
    children: [
      { name: 'Entrada Rápida', href: '/inventory/quick-entry', icon: Zap, permissions: ['read_products'] },
      { name: 'Traspasos', href: '/inventory/transfers', icon: Truck, permissions: ['read_products'] },
      { name: 'Kardex de Inventario', href: '/kardex', icon: ClipboardList, permissions: ['read_products'] },
    ],
  },
  { name: 'Ventas', href: '/sales', icon: FileText, permissions: ['read_sales'] },
  { name: 'Clientes', href: '/customers', icon: Users, permissions: ['read_customers'] },
  { name: 'Caja', href: '/cash-register', icon: DollarSign, permissions: ['read_cash_register'] },
  { name: 'Compras', href: '/purchases', icon: ClipboardList, permissions: ['read_purchases'] },
  { name: 'Proveedores', href: '/suppliers', icon: Truck, permissions: ['read_suppliers'] },
  { name: 'Por Pagar', href: '/supplier-payments', icon: Wallet, permissions: ['read_purchases'] },
  { name: 'Sucursales', href: '/branches', icon: Store, permissions: ['read_branches'] },
  { name: 'Usuarios', href: '/users', icon: Shield, permissions: ['read_users'] },
  { name: 'Promociones', href: '/promotions', icon: Tags, permissions: ['manage_system'] },
  { name: 'Notas de Crédito', href: '/credit-notes', icon: ReceiptText, permissions: ['manage_system'] },
  { name: 'Roles y Permisos', href: '/roles', icon: Shield, permissions: ['read_roles'] },
  { name: 'Auditoría', href: '/audit', icon: ClipboardList, permissions: ['manage_system'] },
  { name: 'Empresas', href: '/companies', icon: Building2, permissions: ['read_companies'] },
  { name: 'Facturación', href: '/invoices', icon: Calculator, permissions: ['read_invoicing'] },
  {
    name: 'Finanzas',
    href: '/accounting/receivables',
    icon: Wallet,
    permissions: ['manage_system'],
    children: [
      { name: 'Por Cobrar', href: '/accounting/receivables', icon: Users, permissions: ['manage_system'] },
      { name: 'Por Pagar', href: '/accounting/payables', icon: Truck, permissions: ['manage_system'] },
      { name: 'Gastos', href: '/accounting/expenses', icon: ReceiptText, permissions: ['manage_system'] },
    ],
  },
  { name: 'Reportes', href: '/reports', icon: BarChart3, permissions: ['read_reports'] },
  { name: 'Configuración', href: '/settings', icon: Settings, permissions: ['manage_system'] },
];

export function SidebarLayout() {
  useSocketSync();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['Productos']);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { isDark, toggle: toggleTheme } = useThemeStore();
  const userRole = user?.role || '';
  const userPermissions = user?.permissions || [];

  const handleLogout = async () => {
    if (!(await dialogs.confirm('¿Estás seguro de cerrar sesión?', { title: 'Cerrar Sesión', confirmText: 'Sí, Cerrar Sesión', cancelText: 'Cancelar' }))) return;
    logout();
  };

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowShortcuts(true);
      }
      if (e.key === 'Escape') {
        setShowShortcuts(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const formatLastLogin = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Hace un momento';
    if (diffMin < 60) return `Hace ${diffMin} min`;
    return date.toLocaleString('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
    });
  };

  const hasPermission = (perms: string[]) => {
    if (userRole === 'SUPER_ADMIN') return true;
    if (perms.length === 0) return true;
    return perms.some((p) => userPermissions.includes(p));
  };

  const navigation = allNavigation
    .map(item => {
      if (item.children) {
        const filteredChildren = item.children.filter((c: any) => hasPermission(c.permissions));
        return { ...item, children: filteredChildren };
      }
      return item;
    })
    .filter(item => {
      if (item.children) return item.children.length > 0;
      return hasPermission(item.permissions);
    });

  const toggleMenu = (name: string) => {
    setExpandedMenus((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const sidebarWidth = collapsed ? 'w-14' : 'w-56';

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100 dark:bg-gray-950">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-50 flex flex-col',
          'transition-all duration-250 ease-in-out',
          sidebarWidth,
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
        style={{
          background: 'linear-gradient(180deg, #1a1f2e 0%, #151922 100%)',
          borderRight: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {/* Logo */}
        <div className={cn(
          'flex items-center h-12 px-3 border-b flex-shrink-0',
          'border-white/10'
        )}>
          {!collapsed ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="w-6 h-6 rounded-md bg-blue-500 flex items-center justify-center flex-shrink-0">
                <ShoppingCart size={13} className="text-white" />
              </div>
              <span className="text-sm font-bold text-white truncate">POS Empresarial</span>
            </div>
          ) : (
            <div className="w-6 h-6 rounded-md bg-blue-500 flex items-center justify-center mx-auto">
              <ShoppingCart size={13} className="text-white" />
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex p-1 rounded-md hover:bg-white/10 transition-colors text-white/40 hover:text-white/80 ml-1 flex-shrink-0"
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-2 px-2 overflow-y-auto overflow-x-hidden space-y-0.5">
          {navigation.map((item: any) => {
            if (item.children) {
              const isExpanded = expandedMenus.includes(item.name);
              const anyChildActive = item.children.some((c: any) => location.pathname === c.href);
              return (
                <div key={item.name}>
                  <button
                    onClick={() => {
                      if (!collapsed) toggleMenu(item.name);
                      else navigate(item.href);
                    }}
                    className={cn(
                      'flex items-center justify-between w-full px-2 py-1.5 rounded-lg text-xs',
                      'transition-all duration-150',
                      anyChildActive
                        ? 'bg-white/10 text-white'
                        : 'text-white/55 hover:text-white/90 hover:bg-white/10',
                    )}
                    style={anyChildActive ? {} : {}}
                  >
                    <div className="flex items-center gap-2">
                      <item.icon size={15} className="flex-shrink-0" />
                      {!collapsed && <span className="font-medium truncate">{item.name}</span>}
                    </div>
                    {!collapsed && (
                      <ChevronDown
                        size={12}
                        className={cn(
                          'transition-transform duration-200 opacity-60',
                          isExpanded ? 'rotate-0' : '-rotate-90',
                        )}
                      />
                    )}
                  </button>
                  {!collapsed && isExpanded && (
                    <div className="ml-3 mt-0.5 space-y-0.5 pl-2 border-l border-white/10">
                      {item.children.map((child: any) => (
                        <button
                          key={child.name}
                          onClick={() => { navigate(child.href); setMobileOpen(false); }}
                          className={cn(
                            'flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-xs',
                            'transition-all duration-150',
                            location.pathname === child.href
                              ? 'text-blue-400 bg-blue-500/10 font-semibold'
                              : 'text-white/50 hover:text-white/80 hover:bg-white/5',
                          )}
                        >
                          <child.icon size={13} className="flex-shrink-0" />
                          <span className="truncate">{child.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            const active = isActive(item.href);
            return (
              <button
                key={item.name}
                onClick={() => { navigate(item.href); setMobileOpen(false); }}
                className={cn(
                  'flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-xs',
                  'transition-all duration-150',
                  active
                    ? 'bg-blue-500/20 text-blue-400 font-semibold'
                    : 'text-white/55 hover:text-white/90 hover:bg-white/10',
                )}
                title={collapsed ? item.name : undefined}
              >
                <item.icon size={15} className="flex-shrink-0" />
                {!collapsed && <span className="truncate">{item.name}</span>}
              </button>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="border-t border-white/10 p-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {user?.fullName?.charAt(0).toUpperCase() || 'U'}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-white/90 truncate">{user?.fullName}</p>
                <p className="text-[10px] text-white/40 truncate">{user?.role}</p>
                <p className="text-[9px] text-white/20 truncate" title={`Último acceso: ${user?.lastLogin ? new Date(user.lastLogin).toLocaleString('es-MX') : '—'}`}>
                  Último acceso: {formatLastLogin(user?.lastLogin)}
                </p>
              </div>
            )}
            <div className="flex flex-col items-center gap-0.5">
              <button
                onClick={() => setShowShortcuts(true)}
                className="p-1.5 rounded-md hover:bg-white/10 text-white/30 hover:text-white/60 transition-colors flex-shrink-0"
                title="Atajos de teclado (Ctrl+K)"
              >
                <Keyboard size={13} />
              </button>
              <button
                onClick={toggleTheme}
                className="p-1.5 rounded-md hover:bg-white/10 text-white/30 hover:text-yellow-400 transition-colors flex-shrink-0"
                title={isDark ? 'Modo claro' : 'Modo oscuro'}
              >
                {isDark ? <Sun size={13} /> : <Moon size={13} />}
              </button>
              <button
                onClick={handleLogout}
                className="p-1.5 rounded-md hover:bg-red-500/20 text-white/40 hover:text-red-400 transition-colors flex-shrink-0"
                title="Cerrar sesión"
              >
                <LogOut size={13} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile burger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed top-3 left-3 z-30 lg:hidden bg-white shadow-ios-md p-2 rounded-lg"
      >
        <Menu size={18} />
      </button>

      {showShortcuts && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowShortcuts(false)}>
          <div className="bg-white rounded-[20px] w-full max-w-sm shadow-ios-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-ios-gray-5">
              <div className="flex items-center gap-2">
                <Keyboard size={18} className="text-ios-gray" />
                <h3 className="font-semibold">Atajos de Teclado</h3>
              </div>
              <button onClick={() => setShowShortcuts(false)} className="p-2 rounded-lg hover:bg-ios-gray-6">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-2">
              {SHORTCUTS.map((s) => (
                <div key={s.key} className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-700">{s.desc}</span>
                  <kbd className="px-2 py-1 bg-gray-100 border border-gray-200 rounded-md text-xs font-mono font-bold text-gray-600 shadow-sm">
                    {s.key}
                  </kbd>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-ios-gray-5">
              <p className="text-[10px] text-ios-gray text-center">
                Presiona <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-200 rounded text-[10px] font-mono">Esc</kbd> para cerrar
              </p>
            </div>
          </div>
        </div>
      )}

      <SessionExpiredModal />
      <ToastContainer />

      {/* ── Main content ── */}
      <main className="flex-1 overflow-y-auto min-w-0">
        {/* Top bar */}
        <div
          className="sticky top-0 z-20 flex items-center px-4 h-11 border-b bg-gray-100/85 dark:bg-gray-900/85 dark:border-gray-800"
          style={{
            backdropFilter: 'blur(12px)',
          }}
        >
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
              {user?.company?.name || 'Empresa'}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span>En línea</span>
          </div>
        </div>

        <div className="p-4 lg:p-5 pt-4">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
